import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import LabelPrintModal from '../components/LabelPrintModal';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const formatDuration = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return 'N/A';
  if (n < 60) return `${Math.round(n)}s`;
  const mins = n / 60;
  if (mins < 60) return `${Math.round(mins)}m`;
  const hrs = mins / 60;
  if (hrs < 48) return `${Math.round(hrs)}h`;
  const days = hrs / 24;
  return `${days.toFixed(1)}d`;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const formatMoneyFromCents = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${(n / 100).toFixed(2)}`;
};

const formatOrderTotal = (order) => {
  const cents = order?.amount_total_cents ?? order?.amount_total;
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return formatMoneyFromCents(n);
};

const buildSparkline = (values, width, height, pad = 8) => {
  const points = [];
  const nums = values.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (nums.length < 2) return '';

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = max - min || 1;

  for (let i = 0; i < nums.length; i += 1) {
    const x = pad + (i * (width - pad * 2)) / (nums.length - 1);
    const y = pad + (1 - (nums[i] - min) / range) * (height - pad * 2);
    points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }

  return points.join(' ');
};

const formatStatus = (status) => {
  const raw = String(status || '').trim();
  if (!raw) return 'Processing';
  const upper = raw.toUpperCase();
  if (upper === 'PENDING_PAYMENT') return 'Open';
  if (upper === 'PAID') return 'Processing';
  if (upper === 'READY_FOR_PICKUP') return 'Ready for pickup';
  if (upper === 'PICKUP_COMPLETE') return 'Pickup complete';
  if (upper === 'CANCELLED') return 'Cancelled';
  if (upper === 'REFUNDED') return 'Refunded';
  const words = raw.replace(/_/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Processing';
};

const statusTone = (status) => {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'SHIPPED' || s === 'PICKUP_COMPLETE') return 'success';
  if (s === 'READY_FOR_PICKUP' || s === 'OPEN' || s === 'PENDING_PAYMENT') return 'info';
  if (s === 'PENDING' || s === 'PROCESSING' || s === 'PAID') return 'warning';
  if (s === 'CANCELLED' || s === 'REFUNDED' || s === 'VOIDED' || s === 'FAILED') return 'danger';
  return 'neutral';
};

const escapeCsv = (val) => {
  const s = String(val ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = ({ filename, rows }) => {
  const header = rows.length ? Object.keys(rows[0]) : [];
  const lines = [header.map(escapeCsv).join(',')];
  for (const row of rows) {
    lines.push(header.map((k) => escapeCsv(row?.[k])).join(','));
  }
  const csv = `${lines.join('\n')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const formatDateTimeEastern = (value) => {
  if (!value) return 'N/A';
  try {
    const dt = typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleString('en-US', { timeZone: 'America/New_York' });
  } catch {
    return 'N/A';
  }
};

const formatEpochSecondsEastern = (epochSeconds) => {
  if (!epochSeconds) return 'N/A';
  const n = Number(epochSeconds);
  if (!Number.isFinite(n) || n <= 0) return 'N/A';
  return formatDateTimeEastern(n * 1000);
};

const normalizeOrderStatus = (status) => {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return 'processing';
  if (s === 'canceled') return 'cancelled';
  return s;
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export default function AdminOrderReports() {
  const navigate = useNavigate();
  const location = useLocation();

  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rangeMode, setRangeMode] = useState('lookback'); // lookback | custom
  const [lookbackUnit, setLookbackUnit] = useState('days'); // hours | days
  const [lookbackValue, setLookbackValue] = useState('7');

  const [generalSettings, setGeneralSettings] = useState(null);
  const [reportType, setReportType] = useState('orders');
  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('ALL');
  const [userRoleFilter, setUserRoleFilter] = useState('ALL');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  const [printOpen, setPrintOpen] = useState(false);
  const [printLabelUrl, setPrintLabelUrl] = useState('');

  const [fulfillmentSummary, setFulfillmentSummary] = useState(null);
  const [fulfillmentMetrics, setFulfillmentMetrics] = useState(null);
  const [bucket, setBucket] = useState('backlog');
  const [backlogOrders, setBacklogOrders] = useState([]);
  const [cursor, setCursor] = useState('');
  const [loadingBacklog, setLoadingBacklog] = useState(false);

  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [markOrder, setMarkOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [force, setForce] = useState(false);
  const [submittingMark, setSubmittingMark] = useState(false);

  const hasRange = Boolean(startDate && endDate);

  useEffect(() => {
    if (reportType === 'fulfillment') return;
    if (rangeMode !== 'lookback') return;

    try {
      const raw = Number(lookbackValue);
      const v = Number.isFinite(raw) ? raw : 7;
      const unit = lookbackUnit === 'hours' ? 'hours' : 'days';
      const ms = unit === 'hours' ? v * 3600 * 1000 : v * 24 * 3600 * 1000;
      const end = new Date();
      const start = new Date(Date.now() - ms);
      setEndDate(toDateInput(end));
      setStartDate(toDateInput(start));
    } catch {
      // ignore
    }
  }, [rangeMode, lookbackUnit, lookbackValue, reportType]);

  const clearOnKeyDown = (e, onClear) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    e.preventDefault();
    if (typeof onClear === 'function') onClear();
  };

  const summary = useMemo(() => {
    const total = orders.length;
    const shipped = orders.filter((o) => normalizeOrderStatus(o.status) === 'shipped').length;
    const cancelled = orders.filter((o) => normalizeOrderStatus(o.status) === 'cancelled').length;
    return { total, shipped, cancelled };
  }, [orders]);

  const userSummary = useMemo(() => {
    const total = users.length;
    const admins = users.filter((u) => String(u.role || '').toLowerCase() === 'admin').length;
    const others = total - admins;
    return { total, admins, others };
  }, [users]);

  const openPrint = (url) => {
    const next = String(url || '').trim();
    if (!next) return;
    setPrintLabelUrl(next);
    setPrintOpen(true);
  };

  const closePrint = () => {
    setPrintOpen(false);
    setPrintLabelUrl('');
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Orders/Reports');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');

        if (API_BASE_URL) {
          try {
            const token = session.getIdToken().getJwtToken();
            const res = await fetch(`${API_BASE_URL}/admin/general`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              setGeneralSettings(data || null);
            }
          } catch {
            setGeneralSettings(null);
          }
        }
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  useEffect(() => {
    if (String(location?.pathname || '').includes('/admin/reports/fulfillment')) {
      setReportType('fulfillment');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  const storefrontMode = useMemo(() => {
    const raw = generalSettings?.settings?.storeMode ?? generalSettings?.storeMode ?? '';
    return String(raw || '').trim().toUpperCase();
  }, [generalSettings]);

  const showTracking = useMemo(() => {
    return String(storefrontMode || '').trim().toUpperCase() !== 'POS';
  }, [storefrontMode]);

  const showPaymentStatusFilter = useMemo(() => {
    const mode = String(storefrontMode || '').trim().toUpperCase();
    return mode === 'POS' || mode === 'HYBRID';
  }, [storefrontMode]);

  const enabledReportTypes = useMemo(() => {
    const raw = generalSettings?.settings?.reports?.enabledReportTypes;
    const allowed = new Set(['orders', 'labels', 'users', 'fulfillment']);
    const list = Array.isArray(raw) ? raw : ['orders', 'labels', 'users', 'fulfillment'];
    const out = [];
    const seen = new Set();
    for (const t of list) {
      const v = String(t || '').trim().toLowerCase();
      if (allowed.has(v) && !seen.has(v)) {
        out.push(v);
        seen.add(v);
      }
    }
    return out.length ? out : ['orders', 'labels', 'users', 'fulfillment'];
  }, [generalSettings]);

  useEffect(() => {
    if (String(location?.pathname || '').includes('/admin/reports/fulfillment')) return;
    if (!enabledReportTypes?.length) return;
    if (enabledReportTypes.includes(reportType)) return;
    setReportType(enabledReportTypes[0]);
    setError('');
    setStatusMessage('');
    setOrders([]);
    setUsers([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledReportTypes, reportType, location?.pathname]);

  const statusOptions = useMemo(() => {
    const mode = String(storefrontMode || '').trim().toUpperCase();
    const isPos = mode === 'POS';
    const isHybrid = mode === 'HYBRID';
    if (isPos) {
      return [
        { k: 'ALL', label: 'ALL' },
        { k: 'Open', label: 'Open' },
        { k: 'Processing', label: 'Processing' },
        { k: 'Ready_for_pickup', label: 'Ready for pickup' },
        { k: 'Pickup_complete', label: 'Pickup complete' },
        { k: 'Cancelled', label: 'Cancelled' },
      ];
    }
    if (isHybrid) {
      return [
        { k: 'ALL', label: 'ALL' },
        { k: 'Shipped', label: 'Shipped' },
        { k: 'Pending', label: 'Pending' },
        { k: 'Cancelled', label: 'Cancelled' },
        { k: 'Processing', label: 'Processing' },
        { k: 'Open', label: 'Open (POS)' },
        { k: 'Ready_for_pickup', label: 'Ready for pickup (POS)' },
        { k: 'Pickup_complete', label: 'Pickup complete (POS)' },
      ];
    }
    return [
      { k: 'ALL', label: 'ALL' },
      { k: 'Shipped', label: 'Shipped' },
      { k: 'Pending', label: 'Pending' },
      { k: 'Cancelled', label: 'Cancelled' },
      { k: 'Processing', label: 'Processing' },
    ];
  }, [storefrontMode]);

  const paymentStatusOptions = useMemo(() => {
    const mode = String(storefrontMode || '').trim().toUpperCase();
    const isPos = mode === 'POS';
    const isHybrid = mode === 'HYBRID';
    if (!isPos && !isHybrid) return [];
    return [
      { k: 'ALL', label: 'ALL' },
      { k: 'Paid', label: isHybrid ? 'Paid (POS)' : 'Paid' },
      { k: 'Pending_payment', label: isHybrid ? 'Pending payment (POS)' : 'Pending payment' },
    ];
  }, [storefrontMode]);

  useEffect(() => {
    const current = String(orderStatusFilter || 'ALL');
    if (current === 'ALL') return;
    if (statusOptions.some((o) => o.k === current)) return;
    setOrderStatusFilter('ALL');
  }, [statusOptions, orderStatusFilter]);

  useEffect(() => {
    if (!showPaymentStatusFilter) {
      if (paymentStatusFilter !== 'ALL') setPaymentStatusFilter('ALL');
      return;
    }
    const current = String(paymentStatusFilter || 'ALL');
    if (current === 'ALL') return;
    if (paymentStatusOptions.some((o) => o.k === current)) return;
    setPaymentStatusFilter('ALL');
  }, [paymentStatusOptions, paymentStatusFilter, showPaymentStatusFilter]);

  const exportResults = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (reportType === 'users') {
      const rows = users.map((u) => ({
        username: u.username || '',
        email: u.email || '',
        role: u.role || '',
        status: u.status || '',
        enabled: u.enabled ? 'Yes' : 'No',
        created_at: u.created_at ? formatDateTimeEastern(u.created_at) : '',
      }));
      downloadCsv({ filename: `users-report-${stamp}.csv`, rows });
      return;
    }
    if (reportType === 'labels') {
      const rows = orders.map((o) => ({
        order_number: o.order_number || '',
        order_date: formatEpochSecondsEastern(o.order_timestamp),
        label_date: formatEpochSecondsEastern(o.fulfillment_updated_at || o.shipped_at),
        label_url: o.label_url || '',
      }));
      downloadCsv({ filename: `labels-report-${stamp}.csv`, rows });
      return;
    }
    const rows = orders.map((o) => {
      const isPosOrder = String(o?.salesChannel || '').trim().toLowerCase() === 'pos' || Boolean(o?.isPos);
      const cents = o.amount_total_cents ?? o.amount_total;
      return {
        order_number: o.order_number || '',
        date: formatEpochSecondsEastern(o.order_timestamp),
        name: o.customer_name || '',
        email: o.customer_email || '',
        channel: o.salesChannel || (o.isPos ? 'POS' : 'ONLINE'),
        payment_status: o.displayPaymentStatus || o.payment_status || o.paymentStatus || '',
        status: formatStatus(o.displayStatus || o.status),
        total: formatOrderTotal(o),
        total_cents: cents ?? '',
        currency: o.currency || '',
        ...(showTracking
          ? {
              tracking_number: isPosOrder ? '' : (o.tracking_number || ''),
              tracking_url: isPosOrder ? '' : (o.tracking_url || ''),
            }
          : {}),
      };
    });
    downloadCsv({ filename: `orders-report-${stamp}.csv`, rows });
  };

  const emailResults = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const subject = `${PROJECT_NAME} ${reportType} report (${stamp})`;
    const lines = [];
    if (reportType === 'users') {
      lines.push(`Users report (${users.length} result(s))`);
      for (const u of users.slice(0, 50)) {
        lines.push(`${u.username || ''} | ${u.email || ''} | ${u.role || ''} | ${u.status || ''}`);
      }
    } else if (reportType === 'labels') {
      lines.push(`Labels report (${orders.length} result(s))`);
      for (const o of orders.slice(0, 50)) {
        lines.push(`${o.order_number || ''} | ${formatEpochSecondsEastern(o.order_timestamp)} | ${o.label_url ? 'Has label' : 'No label'}`);
      }
    } else if (reportType === 'fulfillment') {
      lines.push('Fulfillment report: use web UI for details.');
    } else {
      lines.push(`Orders report (${orders.length} result(s))`);
      for (const o of orders.slice(0, 50)) {
        const channel = o.salesChannel || (o.isPos ? 'POS' : 'ONLINE');
        const status = formatStatus(o.displayStatus || o.status);
        const pay = String(o.displayPaymentStatus || o.payment_status || o.paymentStatus || '').trim();
        lines.push(`${o.order_number || ''} | ${channel} | ${pay} | ${status}`);
      }
    }
    const body = encodeURIComponent(lines.join('\n'));
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  const printResults = () => {
    const title = `${PROJECT_NAME} ${reportType} report`;
    const open = window.open('', '_blank');
    if (!open) return;

    const rows = reportType === 'users'
      ? users.map((u) => ({
          Username: u.username || '',
          Email: u.email || '',
          Role: u.role || '',
          Status: u.status || '',
          Enabled: u.enabled ? 'Yes' : 'No',
          Created: u.created_at ? formatDateTimeEastern(u.created_at) : '',
        }))
      : reportType === 'labels'
        ? orders.map((o) => ({
            'Order Number': o.order_number || '',
            'Order Date': formatEpochSecondsEastern(o.order_timestamp),
            'Label Date': formatEpochSecondsEastern(o.fulfillment_updated_at || o.shipped_at),
            Label: o.label_url ? 'View' : '—',
          }))
        : orders.map((o) => ({
            'Order Number': o.order_number || '',
            Date: formatEpochSecondsEastern(o.order_timestamp),
            Name: o.customer_name || '',
            Email: o.customer_email || '',
            Channel: o.salesChannel || (o.isPos ? 'POS' : 'ONLINE'),
            Payment: String(o.displayPaymentStatus || o.payment_status || o.paymentStatus || '').trim(),
            Total: formatOrderTotal(o),
            Status: formatStatus(o.displayStatus || o.status),
          }));

    const cols = rows.length ? Object.keys(rows[0]) : [];
    const table = `
      <table style="width:100%;border-collapse:collapse;font-family:Arial,sans-serif">
        <thead>
          <tr>
            ${cols.map((c) => `<th style="text-align:left;border-bottom:1px solid #ddd;padding:8px">${c}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) => `
            <tr>
              ${cols.map((c) => `<td style="border-bottom:1px solid #f1f1f1;padding:8px">${String(r[c] ?? '')}</td>`).join('')}
            </tr>`
            )
            .join('')}
        </tbody>
      </table>
    `;

    open.document.write(`
      <html>
        <head>
          <title>${title}</title>
        </head>
        <body>
          <h2 style="font-family:Arial,sans-serif">${title}</h2>
          ${table}
        </body>
      </html>
    `);
    open.document.close();
    open.focus();
    open.print();
  };

  const fetchFulfillmentSummaryAndMetrics = async () => {
    setError('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    try {
      setSearching(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const [summaryRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/fulfillment/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/admin/fulfillment/metrics`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const summaryJson = await summaryRes.json().catch(() => ({}));
      if (!summaryRes.ok) throw new Error(summaryJson?.error || 'Failed to load fulfillment summary');

      const metricsJson = await metricsRes.json().catch(() => ({}));
      if (!metricsRes.ok) throw new Error(metricsJson?.error || 'Failed to load fulfillment metrics');

      setFulfillmentSummary(summaryJson || null);
      setFulfillmentMetrics(metricsJson || null);
    } catch (e) {
      setFulfillmentSummary(null);
      setFulfillmentMetrics(null);
      setError(e?.message || 'Failed to load fulfillment metrics');
    } finally {
      setSearching(false);
    }
  };

  const fetchBacklog = async ({ reset } = { reset: false }) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    try {
      setLoadingBacklog(true);
      setError('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const url = new URL(`${API_BASE_URL}/admin/fulfillment/backlog`);
      url.searchParams.set('bucket', bucket);
      url.searchParams.set('limit', '50');
      if (!reset && cursor) {
        url.searchParams.set('cursor', cursor);
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load backlog');

      const list = Array.isArray(data?.orders) ? data.orders : [];
      setBacklogOrders((prev) => (reset ? list : [...prev, ...list]));
      setCursor(String(data?.next_cursor || ''));
    } catch (e) {
      setError(e?.message || 'Failed to load backlog');
      if (reset) {
        setBacklogOrders([]);
        setCursor('');
      }
    } finally {
      setLoadingBacklog(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (reportType !== 'fulfillment') return;
    fetchFulfillmentSummaryAndMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, loading]);

  useEffect(() => {
    if (loading) return;
    if (reportType !== 'fulfillment') return;
    setBacklogOrders([]);
    setCursor('');
    fetchBacklog({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, reportType, loading]);

  const openMarkModal = (order) => {
    setMarkOrder(order);
    setTrackingNumber(String(order?.tracking_number || ''));
    setCarrier(String(order?.carrier || ''));
    setSendEmail(true);
    setForce(false);
    setMarkModalOpen(true);
  };

  const closeMarkModal = () => {
    setMarkModalOpen(false);
    setMarkOrder(null);
    setTrackingNumber('');
    setCarrier('');
    setSendEmail(true);
    setForce(false);
    setSubmittingMark(false);
  };

  const submitMarkShipped = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    const orderNumber = String(markOrder?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order number.');
      return;
    }

    const tn = String(trackingNumber || '').trim();
    if (!tn) {
      setError('Tracking number is required.');
      return;
    }

    try {
      setSubmittingMark(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/mark-shipped`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          tracking_number: tn,
          carrier: String(carrier || '').trim(),
          send_email: sendEmail,
          force,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to mark shipped'));

      setStatusMessage(`Order ${orderNumber} marked shipped (${data?.email_status || 'email skipped'}).`);
      setBacklogOrders((prev) => prev.filter((o) => String(o?.order_number || '').trim() !== orderNumber));
      closeMarkModal();
      await fetchFulfillmentSummaryAndMetrics();
      await fetchBacklog({ reset: true });
    } catch (e) {
      setError(e?.message || 'Failed to mark shipped');
    } finally {
      setSubmittingMark(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch {
      // ignore
    }
  };

  const runOrdersReport = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please set start and end date.');
      return;
    }

    try {
      setSearching(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const url = new URL(`${API_BASE_URL}/admin/orders/report`);
      url.searchParams.set('start', startDate);
      url.searchParams.set('end', endDate);
      const forceStatus = reportType === 'labels' ? 'Shipped' : '';
      if (forceStatus) {
        url.searchParams.set('status', forceStatus);
      } else if (showPaymentStatusFilter && paymentStatusFilter && paymentStatusFilter !== 'ALL') {
        url.searchParams.set('status', paymentStatusFilter);
      } else if (orderStatusFilter && orderStatusFilter !== 'ALL') {
        url.searchParams.set('status', orderStatusFilter);
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to run report');

      const list = Array.isArray(data.orders) ? data.orders : [];
      setOrders(reportType === 'labels' ? list.filter((o) => String(o?.label_url || '').trim()) : list);
      setUsers([]);
      setStatusMessage(`Loaded ${Array.isArray(data.orders) ? (reportType === 'labels' ? list.filter((o) => String(o?.label_url || '').trim()).length : list.length) : 0} order(s).`);
    } catch (e) {
      setError(e?.message || 'Failed to run report');
      setOrders([]);
      setUsers([]);
    } finally {
      setSearching(false);
    }
  };

  const runUsersReport = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please set start and end date.');
      return;
    }

    try {
      setSearching(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const url = new URL(`${API_BASE_URL}/admin/users/report`);
      url.searchParams.set('start', startDate);
      url.searchParams.set('end', endDate);
      if (userRoleFilter && userRoleFilter !== 'ALL') {
        url.searchParams.set('role', userRoleFilter);
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to run users report');

      setUsers(Array.isArray(data.users) ? data.users : []);
      setOrders([]);
      setStatusMessage(`Loaded ${Array.isArray(data.users) ? data.users.length : 0} user(s).`);
    } catch (e) {
      setError(e?.message || 'Failed to run users report');
      setUsers([]);
      setOrders([]);
    } finally {
      setSearching(false);
    }
  };

  const runReport = async () => {
    if (reportType === 'fulfillment') {
      await fetchFulfillmentSummaryAndMetrics();
      await fetchBacklog({ reset: true });
      return undefined;
    }
    if (reportType === 'users') {
      return runUsersReport();
    }
    return runOrdersReport();
  };

  const triggerBackfill = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    try {
      setBackfilling(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/backfill-gsi`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to start backfill'));

      setStatusMessage('Backfill started. It will continue in the background.');
    } catch (e) {
      setError(e?.message || 'Failed to start backfill');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-chart-line"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Reports</h1>
            <p>Search orders, users, and fulfillment</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <nav className="reports-subtabs" aria-label="Reports navigation">
        <NavLink
          to="/admin/reports/orders"
          className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}
        >
          Orders
        </NavLink>
        <NavLink
          to="/admin/reports/online-sales"
          className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}
        >
          Online Sales
        </NavLink>
        <NavLink
          to="/admin/reports/fulfillment"
          className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}
        >
          Fulfillment
        </NavLink>
      </nav>

      <div className="admin-content">
        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        <div className="product-form-container">
          <h2>Report Filters</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Report type</label>
              <select
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  setError('');
                  setStatusMessage('');
                  setOrders([]);
                  setUsers([]);
                }}
              >
                {enabledReportTypes.includes('orders') ? <option value="orders">Orders report</option> : null}
                {enabledReportTypes.includes('labels') ? <option value="labels">Labels report</option> : null}
                {enabledReportTypes.includes('users') ? <option value="users">Users report</option> : null}
                {enabledReportTypes.includes('fulfillment') ? <option value="fulfillment">Fulfillment</option> : null}
              </select>
            </div>

          {reportType === 'orders' ? (
            <div className="form-group">
              <label>Status</label>
              <select
                value={orderStatusFilter}
                onChange={(e) => setOrderStatusFilter(e.target.value)}
              >
                {statusOptions.map((opt) => (
                  <option key={`status-${opt.k}`} value={opt.k}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : reportType === 'labels' ? (
            <div className="form-group">
              <label>Status</label>
              <input value="Shipped (with labels)" disabled />
            </div>
          ) : reportType === 'users' ? (
            <div className="form-group">
              <label>Role</label>
              <select
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
              >
                <option value="ALL">ALL</option>
                <option value="Admin">Admin</option>
                <option value="Others">Others</option>
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label>Backlog bucket</label>
              <select
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
              >
                <option value="backlog">Backlog</option>
                <option value="overdue">Overdue</option>
                <option value="missing_tracking">Missing tracking</option>
              </select>
            </div>
          )}

          {reportType === 'orders' && showPaymentStatusFilter ? (
            <div className="form-group">
              <label>Payment status</label>
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
              >
                {paymentStatusOptions.map((opt) => (
                  <option key={`payment-status-${opt.k}`} value={opt.k}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          </div>

          {reportType !== 'fulfillment' ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Range mode</label>
                  <select
                    value={rangeMode}
                    onChange={(e) => setRangeMode(e.target.value === 'custom' ? 'custom' : 'lookback')}
                  >
                    <option value="lookback">Lookback</option>
                    <option value="custom">Custom dates</option>
                  </select>
                </div>

                {rangeMode === 'lookback' ? (
                  <>
                    <div className="form-group">
                      <label>Lookback</label>
                      <input
                        type="number"
                        min="1"
                        max={lookbackUnit === 'hours' ? '720' : '365'}
                        value={lookbackValue}
                        onChange={(e) => setLookbackValue(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Unit</label>
                      <select
                        value={lookbackUnit}
                        onChange={(e) => setLookbackUnit(e.target.value === 'hours' ? 'hours' : 'days')}
                      >
                        <option value="days">Days</option>
                        <option value="hours">Hours</option>
                      </select>
                    </div>
                  </>
                ) : null}
              </div>

              {rangeMode === 'lookback' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label>Start date</label>
                    <input type="date" value={startDate} disabled />
                  </div>
                  <div className="form-group">
                    <label>End date</label>
                    <input type="date" value={endDate} disabled />
                  </div>
                </div>
              ) : null}

              {rangeMode === 'custom' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label>Start date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setRangeMode('custom');
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>End date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setRangeMode('custom');
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          <div className="form-actions">
            {reportType === 'orders' ? (
              <button type="button" className="btn-secondary" disabled={backfilling} onClick={triggerBackfill}>
                {backfilling ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Starting backfill...
                  </>
                ) : (
                  <>
                    <i className="fas fa-database"></i>
                    Backfill index
                  </>
                )}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-submit"
              disabled={searching || (reportType !== 'fulfillment' && !hasRange)}
              onClick={runReport}
            >
              {searching ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className={reportType === 'fulfillment' ? 'fas fa-sync' : 'fas fa-search'}></i>
                  {reportType === 'fulfillment' ? 'Refresh' : 'Run report'}
                </>
              )}
            </button>

            {reportType !== 'fulfillment' && ((reportType === 'users' && users.length) || (reportType !== 'users' && orders.length)) ? (
              <div className="report-actions">
                <button type="button" className="admin-action-btn" onClick={exportResults} disabled={searching}>
                  Export CSV
                </button>
                <button type="button" className="admin-action-btn" onClick={emailResults} disabled={searching}>
                  Email
                </button>
                <button type="button" className="admin-action-btn" onClick={printResults} disabled={searching}>
                  Print
                </button>
              </div>
            ) : null}
          </div>

          {reportType === 'orders' && orders.length ? (
            <div style={{ marginTop: 14, color: '#667085' }}>
              Total: {summary.total} | Shipped: {summary.shipped} | Cancelled: {summary.cancelled}
            </div>
          ) : null}

          {reportType === 'users' && users.length ? (
            <div style={{ marginTop: 14, color: '#667085' }}>
              Total: {userSummary.total} | Admin: {userSummary.admins} | Others: {userSummary.others}
            </div>
          ) : null}
        </div>

        {reportType === 'fulfillment' ? (
          <>
            <section className="fulfillment-kpi-grid">
              <div className="fulfillment-kpi-card">
                <div className="fulfillment-kpi-title">Backlog</div>
                <div className="fulfillment-kpi-value">{Number(fulfillmentSummary?.kpis?.backlog || 0).toLocaleString()}</div>
                <div className="fulfillment-kpi-sub">Paid and not shipped</div>
              </div>
              <div className="fulfillment-kpi-card">
                <div className="fulfillment-kpi-title">Overdue</div>
                <div className="fulfillment-kpi-value">{Number(fulfillmentSummary?.kpis?.overdue || 0).toLocaleString()}</div>
                <div className="fulfillment-kpi-sub">Older than 48 hours</div>
              </div>
              <div className="fulfillment-kpi-card">
                <div className="fulfillment-kpi-title">Shipped today</div>
                <div className="fulfillment-kpi-value">{Number(fulfillmentSummary?.kpis?.shipped_today || 0).toLocaleString()}</div>
                <div className="fulfillment-kpi-sub">America/New_York</div>
              </div>
              <div className="fulfillment-kpi-card">
                <div className="fulfillment-kpi-title">Median time to ship</div>
                <div className="fulfillment-kpi-value">{formatDuration(fulfillmentSummary?.kpis?.median_time_to_ship_seconds)}</div>
                <div className="fulfillment-kpi-sub">Paid → shipped</div>
              </div>
              <div className="fulfillment-kpi-card">
                <div className="fulfillment-kpi-title">P90 time to ship</div>
                <div className="fulfillment-kpi-value">{formatDuration(fulfillmentSummary?.kpis?.p90_time_to_ship_seconds)}</div>
                <div className="fulfillment-kpi-sub">Tail performance</div>
              </div>
            </section>

            <section className="fulfillment-charts">
              <div className="fulfillment-chart-card">
                <div className="fulfillment-chart-header">
                  <h3>Backlog trend (last 30 days)</h3>
                  <button className="admin-action-btn" onClick={fetchFulfillmentSummaryAndMetrics}>Refresh</button>
                </div>

                {(() => {
                  const list = Array.isArray(fulfillmentMetrics?.series) ? fulfillmentMetrics.series : [];
                  const series = list.slice(-30);
                  const backlogValues = series.map((d) => d.backlog || 0);
                  const paidValues = series.map((d) => d.paid || 0);
                  const shippedValues = series.map((d) => d.shipped || 0);
                  const sparklinePoints = buildSparkline(backlogValues, 560, 120);
                  const maxBar = Math.max(
                    0,
                    ...[...paidValues, ...shippedValues]
                      .map((v) => Number(v))
                      .filter((v) => Number.isFinite(v))
                  );

                  return (
                    <div className="fulfillment-chart-body">
                      {sparklinePoints ? (
                        <svg width="100%" height="140" viewBox="0 0 560 140" role="img" aria-label="Backlog trend">
                          <rect x="0" y="0" width="560" height="140" rx="14" fill="#f8fafc" />
                          <polyline points={sparklinePoints} fill="none" stroke="#7c3aed" strokeWidth="3" />
                        </svg>
                      ) : (
                        <div className="fulfillment-empty">Not enough data yet.</div>
                      )}

                      <div className="fulfillment-bars">
                        {series.slice(-14).map((d) => {
                          const paid = Number(d.paid || 0);
                          const shipped = Number(d.shipped || 0);
                          const paidH = maxBar ? clamp((paid / maxBar) * 48, 0, 48) : 0;
                          const shippedH = maxBar ? clamp((shipped / maxBar) * 48, 0, 48) : 0;
                          return (
                            <div key={d.date} className="fulfillment-bar">
                              <div className="fulfillment-bar-stack">
                                <div className="fulfillment-bar-paid" style={{ height: `${paidH}px` }} title={`Paid: ${paid}`} />
                                <div className="fulfillment-bar-shipped" style={{ height: `${shippedH}px` }} title={`Shipped: ${shipped}`} />
                              </div>
                              <div className="fulfillment-bar-label">{String(d.date || '').slice(5)}</div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="fulfillment-legend">
                        <div className="fulfillment-legend-item"><span className="legend-swatch paid" />Paid</div>
                        <div className="fulfillment-legend-item"><span className="legend-swatch shipped" />Shipped</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </section>

            <section className="fulfillment-table-card">
              <div className="fulfillment-table-header">
                <div>
                  <h3>Backlog</h3>
                  <p className="fulfillment-table-sub">Drilldown list for operational follow-up</p>
                </div>

                <div className="fulfillment-controls">
                  <button className="admin-action-btn" onClick={() => fetchBacklog({ reset: true })} disabled={loadingBacklog}>
                    Refresh
                  </button>
                </div>
              </div>

              <div className="fulfillment-table-scroll">
                <table className="fulfillment-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Paid at</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Carrier</th>
                      {showTracking ? <th>Tracking</th> : null}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backlogOrders.map((o) => {
                      const orderNum = String(o.order_number || '').trim();
                      const tracking = String(o.tracking_number || '').trim();
                      const trackUrl = String(o.tracking_url || '').trim();
                      const isPosOrder = String(o?.salesChannel || '').trim().toLowerCase() === 'pos' || Boolean(o?.isPos);
                      return (
                        <tr key={`${orderNum}-${o.order_timestamp || ''}`}>
                          <td className="mono" data-label="Order">{orderNum || '—'}</td>
                          <td data-label="Paid at">{formatEpochSecondsEastern(o.paid_at)}</td>
                          <td data-label="Status">{String(o.status || 'Processing')}</td>
                          <td data-label="Total">{formatMoneyFromCents(o.amount_total)}</td>
                          <td data-label="Carrier">{String(o.carrier || '—')}</td>
                          {showTracking ? (
                            <td data-label="Tracking">
                              {!isPosOrder && tracking ? (
                                trackUrl ? (
                                  <a href={trackUrl} target="_blank" rel="noreferrer">{tracking}</a>
                                ) : (
                                  <span className="mono">{tracking}</span>
                                )
                              ) : (
                                '—'
                              )}
                            </td>
                          ) : null}
                          <td data-label="Actions">
                            <button className="admin-mini-btn" onClick={() => openMarkModal(o)}>
                              Mark shipped
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {!backlogOrders.length && !loadingBacklog ? (
                      <tr>
                        <td colSpan={7} className="fulfillment-empty-row">No orders in this bucket.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div className="fulfillment-table-footer">
                <button
                  className="admin-action-btn"
                  onClick={() => fetchBacklog({ reset: false })}
                  disabled={loadingBacklog || !cursor}
                >
                  {cursor ? 'Load more' : 'No more'}
                </button>
              </div>
            </section>

            {markModalOpen ? (
              <div className="fulfillment-modal-overlay" role="dialog" aria-modal="true">
                <div className="fulfillment-modal">
                  <div className="fulfillment-modal-header">
                    <h3>Mark shipped</h3>
                    <button className="admin-mini-btn" onClick={closeMarkModal}>
                      Close
                    </button>
                  </div>

                  <div className="fulfillment-modal-body">
                    <div className="fulfillment-modal-row">
                      <label>Order</label>
                      <div className="mono">{String(markOrder?.order_number || '')}</div>
                    </div>

                    <div className="fulfillment-modal-row">
                      <label>Tracking number</label>
                      <input
                        className="card-input"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="e.g. 1Z..."
                      />
                    </div>

                    <div className="fulfillment-modal-row">
                      <label>Carrier (optional)</label>
                      <input
                        className="card-input"
                        value={carrier}
                        onChange={(e) => setCarrier(e.target.value)}
                        placeholder="UPS / USPS / FEDEX / DHL"
                      />
                    </div>

                    <div className="fulfillment-modal-checkbox">
                      <label>
                        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                        Send customer email
                      </label>
                    </div>

                    <div className="fulfillment-modal-checkbox">
                      <label>
                        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                        Force update even if already shipped
                      </label>
                    </div>
                  </div>

                  <div className="fulfillment-modal-footer">
                    <button className="admin-action-btn" onClick={submitMarkShipped} disabled={submittingMark}>
                      {submittingMark ? 'Saving…' : 'Mark shipped'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="products-list-container">
            <h2>{reportType === 'users' ? 'Users' : reportType === 'labels' ? 'Labels' : 'Orders'}</h2>
            {reportType === 'users' ? (
            users.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-users"></i>
                <p>No users found.</p>
              </div>
            ) : (
              <div className="audit-table-wrapper users-report-table">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Enabled</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={`${user.username}-${user.created_at}`}
                      >
                        <td data-label="Username">{user.username || ''}</td>
                        <td data-label="Email">{user.email || ''}</td>
                        <td data-label="Role">{user.role || ''}</td>
                        <td data-label="Status">{user.status || ''}</td>
                        <td data-label="Enabled">{user.enabled ? 'Yes' : 'No'}</td>
                        <td data-label="Created">{user.created_at ? formatDateTimeEastern(user.created_at) : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
            ) : orders.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-receipt"></i>
              <p>No orders found.</p>
            </div>
          ) : (
            <div
              className={`audit-table-wrapper ${
                reportType === 'labels'
                  ? 'labels-report-table'
                  : reportType === 'orders'
                    ? 'orders-report-table'
                    : ''
              }`}
            >
              <table className="audit-table">
                <thead>
                  <tr>
                    {reportType === 'labels' ? (
                      <>
                        <th>Order Number</th>
                        <th>Order Date</th>
                        <th>Label Date</th>
                        <th>Label</th>
                      </>
                    ) : (
                      <>
                        <th>Order Number</th>
                        <th>Date</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Payment</th>
                        <th>Total</th>
                        <th>Status</th>
                        {showTracking ? <th>Tracking</th> : null}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={`${order.order_number}-${order.order_timestamp}`}>
                      {reportType === 'labels' ? (
                        <>
                          <td className="mono" data-label="Order Number">{order.order_number || ''}</td>
                          <td data-label="Order Date">{formatEpochSecondsEastern(order.order_timestamp)}</td>
                          <td data-label="Label Date">{formatEpochSecondsEastern(order.fulfillment_updated_at || order.shipped_at)}</td>
                          <td data-label="Label">
                            {order.label_url ? (
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                <a href={order.label_url} target="_blank" rel="noreferrer">
                                  View
                                </a>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openPrint(order.label_url);
                                  }}
                                >
                                  Print
                                </a>
                              </div>
                            ) : (
                              <span style={{ color: '#777' }}>—</span>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <td data-label="Order Number">{order.order_number || ''}</td>
                          <td data-label="Date">{formatEpochSecondsEastern(order.order_timestamp)}</td>
                          <td data-label="Name">{order.customer_name || ''}</td>
                          <td data-label="Email">{order.customer_email || ''}</td>
                          <td data-label="Payment">
                            <span className={`status-pill ${statusTone(order.displayPaymentStatus || order.payment_status || order.paymentStatus)}`}>
                              {String(order.displayPaymentStatus || order.payment_status || order.paymentStatus || '—') || '—'}
                            </span>
                          </td>
                          <td data-label="Total">{formatOrderTotal(order)}</td>
                          <td data-label="Status">
                            <span className={`status-pill ${statusTone(order.displayStatus || order.status)}`}>
                              {formatStatus(order.displayStatus || order.status)}
                            </span>
                          </td>
                          {showTracking ? (
                            <td data-label="Tracking">
                              {!(String(order?.salesChannel || '').trim().toLowerCase() === 'pos' || Boolean(order?.isPos)) && order.tracking_number && order.tracking_url ? (
                                <a href={order.tracking_url} target="_blank" rel="noreferrer" className="order-number-link">
                                  {order.tracking_number}
                                </a>
                              ) : (
                                <span style={{ color: '#777' }}>—</span>
                              )}
                            </td>
                          ) : null}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}
      </div>

      <LabelPrintModal open={printOpen} labelUrl={printLabelUrl} onClose={closePrint} />
    </div>
  );
}
